import { ArrowRightOutlined, HistoryOutlined } from '@ant-design/icons';
import { Popover, Select, Switch } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import Info from 'assets/info.svg?react';
import DataMatcherHeader from 'components/DataMatcherHeader/Header';
import { observer } from 'mobx-react';
import { Option as OptionType } from 'models';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonType,
  Card,
  Checkbox,
  Col,
  Layout,
  Row,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'syngenta-digital-cropwise-react-ui-kit';
import { EmptyView } from '../Interstitial';
import InfoIcon from './../../assets/info_24px.svg';
import InstanceCardList from './components/InstanceCardList';
import ResourceMasterListSelect from './components/ResourceMasterListSelect';
import UnmappedResourceList from './components/UnmappedResourceList';
import './style.less';
import { Instance, Product, ResourceType } from './types';

import { RootContext } from 'RootProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  UnitSystemTypes,
  useUnitSystemStore,
} from 'modules/yield-analysis/store/useUnitSystemStore';
import { getBooleanFeatureFlagValues } from 'tools/feature-flags/launchdarkly-flags';
import MatchingHistoryModal from 'views/ResourceMapper/components/MatchingHistoryModal';
import WarningIcon from '../../assets/error_outline2_24px.svg?react';
import { APIState } from '../../models/APIState';
import {
  AmplitudeEventEnum,
  AmplitudePropertyKeyEnum,
  useAmplitude,
} from '../../services/amplitudeHelper';
import CreateNewField from './components/CreateNewField';
import UnmappedResourceFiltersButton from './components/UnmappedResourceFilters/UnmappedResourceFiltersButton';
import { useResourceMapperStore } from './store/useResourceMapperStore';
import { MisalignmentBanner } from './components/MisalignmentBanner';

const { Content, Sider, Header, Footer } = Layout;
const { Option } = Select;
const { Text } = Typography;

export enum DataState {
  NO_DATA_TO_MAP,
  EMPTY_DATA_SET,
}

type IntegrationOptionProps = { value: string; label: string; length: number };
type ResourceMapperViewViewProps = {
  integrationOptions: IntegrationOptionProps[];
  resourceTypeOptions: OptionType<ResourceType>[];
};

export default observer(function ResourceMapperView({
  integrationOptions,
}: ResourceMapperViewViewProps) {
  const [selectedUnmapped, setSelectedUnmapped] = useState<any>();
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<string[]>([]);
  const [allSelectedChecked, setAllSelectedChecked] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [visibleCreateNewField] = useState<boolean>(false);
  const [dropdownClassName] = useState<string>('');
  const { t } = useTranslation();
  const { auth, root } = useContext(RootContext);
  const prevInstances = useRef<Instance[]>([]);
  const cardBodyRef = useRef<HTMLDivElement | null>(null);
  const connectAuth = auth.connectAuth;
  const [emptyDataState, setEmptyDataState] = useState(DataState.NO_DATA_TO_MAP);
  const emptyDataMessage =
    emptyDataState === DataState.NO_DATA_TO_MAP
      ? t('resource-mapper.all_matched')
      : t('resource-mapper.no_operation');
  const emptyDataDetail =
    emptyDataState === DataState.NO_DATA_TO_MAP
      ? t('resource-mapper.to_see_previous')
      : t('resource-mapper.no_operation_detail');
  const disableIntegrationSelect = Boolean(emptyDataState === DataState.EMPTY_DATA_SET);
  const [isOpenIgnorePopover, setOpenIgnorePopover] = useState(false);
  const flags = useFlags();
  const { showFutureOperationsCheckbox, showNoFieldMatchCheckbox, enableFieldMatchingPage } =
    getBooleanFeatureFlagValues(flags);
  const {
    activeResource,
    allOperationIds,
    selectedInstances,
    activeResourceType,
    autoMappingEnabled,
    productsMasterList,
    loadInstancesStatus,
    selectableInstances,
    instanceProductsMap,
    isUpdatingResources,
    selectedIntegration,
    resourceSelectStatus,
    isLoadingIgnoreProduct,
    selectedMasterProductId,
    unmappedResourcesStatus,
    loadInstanceProductsStatus,
    isLoadingProductsMasterList,
    isGetProductsNeedsResolving,
    handleIgnoreProduct,
    isShowFooterOptions,
    addSelectedInstance,
    clickUpdateResources,
    setActiveResourceById,
    setAutoMappingEnabled,
    setSelectedIntegration,
    removeSelectedInstance,
    loadProductsMasterList,
    addAllResourceInstances,
    resetProductsByOperations,
    removeAllSelectedInstances,
    setSelectedMasterProductId,
    getFilteredUnmappedResources,
    setIsGetProductsNeedsResolving,
  } = useResourceMapperStore();

  const prevResource = useRef<Product | null>(activeResource);
  const [currentUnitSystem, setCurrentUnitSystem] = useState<UnitSystemTypes | null>(null);
  const { unitSystem } = useUnitSystemStore();

  const filteredUnmappedResources = getFilteredUnmappedResources();

  const trackEvent = useAmplitude();

  useEffect(() => {
    if (!currentUnitSystem && unitSystem) {
      setCurrentUnitSystem(unitSystem);
    }
    if (currentUnitSystem !== unitSystem) {
      setCurrentUnitSystem(unitSystem);
      resetProductsByOperations();
    }
  }, [unitSystem, currentUnitSystem, setCurrentUnitSystem, resetProductsByOperations]);

  useEffect(() => {
    if (!auth.activeOrganization?.id || productsMasterList !== null) return;
    void loadProductsMasterList(auth.activeOrganization?.source, auth.activeOrganization?.country);
  }, [
    loadProductsMasterList,
    isLoadingProductsMasterList,
    auth.activeOrganization,
    productsMasterList,
  ]);

  const handleOpenChangeIgnorePopover = (newOpen: boolean) => {
    setOpenIgnorePopover(newOpen);
  };

  useEffect(() => {
    const resetInstances = () => {
      setAllSelectedChecked(false);
      setAllSelected([]);
    };
    if (prevResource.current?.product_id !== activeResource?.product_id) {
      resetInstances();
    } else {
      if (prevInstances.current.length !== selectableInstances.length) {
        resetInstances();
      }
    }
    setSelectedUnmapped(activeResource?.product_id);
    prevResource.current = activeResource;
    prevInstances.current = selectableInstances;
  }, [activeResource, selectableInstances]);

  useEffect(() => {
    if (!selectableInstances) return;
    if (!allSelected.length) {
      setIndeterminate(false);
      setAllSelectedChecked(false);
      return;
    }
    if (allSelected.length === selectableInstances.length) {
      setAllSelectedChecked(true);
      setIndeterminate(false);
      return;
    }
    if (allSelected.length < selectableInstances.length) {
      setIndeterminate(true);
      setAllSelectedChecked(false);
    }
  }, [allSelected.length, selectableInstances]);

  useEffect(() => {
    if (allOperationIds.length === 0) {
      setEmptyDataState(DataState.EMPTY_DATA_SET);
      return;
    }
    setEmptyDataState(DataState.NO_DATA_TO_MAP);
  }, [allOperationIds]);

  const onIntegrationChange = (option: string) => {
    setSelectedIntegration(option);
  };

  const onSelectUnmapped = (e: any) => {
    setActiveResourceById(e.target.value, root.getCropwiseAuthToken().access_token);
    setSelectedUnmapped(e.target.value);
  };

  const onHeaderClick = (instance: any, e: boolean) => {
    let ids = [];
    if (e) {
      ids = [...allSelected, instance.id];
      addSelectedInstance(instance);
    } else {
      ids = allSelected.filter((id) => id !== instance.id);
      removeSelectedInstance(instance);
    }
    setAllSelectedChecked(false);
    setAllSelected(ids);
  };

  const onAllChanged = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const keys: string[] =
        selectableInstances?.map((instance: Pick<Instance, 'id'>) => instance.id) ?? [];
      setAllSelected(keys);
      addAllResourceInstances();
    } else {
      clearAllSelection();
    }
    setAllSelectedChecked(e.target.checked);
  };

  const clearAllSelection = () => {
    setAllSelected([]);
    removeAllSelectedInstances();
    setAllSelectedChecked(false);
  };

  const onChangeSaveMappings = ({ target: { checked } }: any) => {
    setAutoMappingEnabled(checked);
  };

  const trackIntegrationEvent = (eventType: AmplitudeEventEnum) => (): void =>
    trackEvent(eventType, {
      [AmplitudePropertyKeyEnum.INTEGRATION_NAME]: selectedIntegration,
    });

  const trackIgnoreProduct = trackIntegrationEvent(AmplitudeEventEnum.IGNORE_PRODUCT);

  const trackChangeUnmappedProduct = trackIntegrationEvent(
    AmplitudeEventEnum.SELECT_PRODUCT_TO_MATCH,
  );

  const trackSuccessfullyMatchProduct = trackIntegrationEvent(
    AmplitudeEventEnum.SUCCESSFULLY_MATCH_PRODUCT,
  );

  const trackMatchProducts = useCallback(() => {
    const selectableInstancesCount = selectableInstances.length;
    const selectedInstancesCount = selectedInstances.length;
    return trackEvent(AmplitudeEventEnum.MATCH_PRODUCT, {
      [AmplitudePropertyKeyEnum.INTEGRATION_NAME]: selectedIntegration,
      [AmplitudePropertyKeyEnum.OPERATIONS_SELECTED]:
        selectableInstancesCount === selectedInstancesCount ? 'ALL' : selectedInstancesCount,
      [AmplitudePropertyKeyEnum.APPLIED_TO_FUTURE_OPS]: autoMappingEnabled,
    });
  }, [selectableInstances, selectedInstances, selectedIntegration, autoMappingEnabled, trackEvent]);

  const handleUpdate = useCallback(async () => {
    trackMatchProducts();
    const updated = await clickUpdateResources(false);

    if (updated?.successes?.length) {
      trackSuccessfullyMatchProduct();
    }
  }, [trackMatchProducts, trackSuccessfullyMatchProduct, clickUpdateResources]);

  const handleCancelCreateNewField = useCallback(() => {}, []);

  const handleClickCreateNewField = useCallback(() => {}, []);

  const generatedUnmappedResourceOptions = () => {
    return integrationOptions.map(({ value, label, length }: IntegrationOptionProps) => {
      return (
        <Option
          data-cy={`integration_select_${value}`}
          value={value}
          label={label}
          key={value}
          disabled={disableIntegrationSelect}
        >
          <Row className="mt-2">
            <div className="w-20px h-18px bg-neutral-50 rounded-4 mt-3 mr-8 text-white text-center">
              <div className="text-12 leading-12px mt-3">{length}</div>
            </div>
            {label}
          </Row>
        </Option>
      );
    });
  };

  const handleShowProductsNoFieldMatchChange = (checked: boolean) => {
    setIsGetProductsNeedsResolving(checked);
  };

  const isLoading = unmappedResourcesStatus === APIState.LOADING;
  const isLoadingResourceSelect = resourceSelectStatus === APIState.LOADING || isLoading;
  const isEmpty = unmappedResourcesStatus === APIState.EMPTY;
  const isEmptySelectedOperations = selectedInstances.length === 0;
  const isNotSelectedCropwiseResource = !selectedMasterProductId;

  const isSubmitDisabled = [
    isLoading,
    isEmpty,
    isUpdatingResources,
    isLoadingProductsMasterList,
    isEmptySelectedOperations,
    isNotSelectedCropwiseResource,
  ].some(Boolean);

  const unresolvedToggle = (
    <div className="mt-16 mb-8 bg-neutral-10 border-neutral-20 border-1 rounded-8 px-5 py-10 text-left pl-10 flex items-center">
      <Switch
        data-cy="toggle_needs_resolving"
        defaultChecked={isGetProductsNeedsResolving}
        onChange={handleShowProductsNoFieldMatchChange}
        size="small"
        disabled={isLoading}
      />
      <span className="text-12 font-sans pl-4 pr-4">{t('Show products with no field match')}</span>
      <Tooltip title={t('Show products from applications registered without fields')}>
        <img src={InfoIcon} alt="InfoIcon" width="16px" height="16px" className="inline" />
      </Tooltip>
    </div>
  );

  const futureMappingsCheckbox = (
    <div className="m-auto mr-26 pt-1 flex">
      <Checkbox
        data-testid="auto_mapping_enabled_checkbox"
        checked={autoMappingEnabled}
        onChange={onChangeSaveMappings}
      >
        {t('resource-mapper.apply_future')}
      </Checkbox>
      <Tooltip placement="topLeft" title={t('resource-mapper.apply_future_info')}>
        <Info className="data-matching-info-icon" />
      </Tooltip>
    </div>
  );

  return (
    <>
      <Layout className="flex flex-row h-screen w-100% bg-gray-semilight relative">
        <Sider
          width="25%"
          className="h-screen bg-white text-bold resource-mapper-sider"
          style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)' }}
        >
          <Layout className="h-100% bg-white flex flex-col">
            <DataMatcherHeader
              connectAuth={connectAuth}
              handleShowModal={() => setIsHistoryModalOpen(true)}
            />
            <Content className="flex-1 flex flex-col mx-24">
              <div className="flex items-end gap-4">
                <div className="w-100%">
                  <div className="mt-16">
                    <Text>{t('resource-mapper.integration')}</Text>
                  </div>
                  <Select
                    data-cy="integration_select"
                    className="w-100% custom-select h-40px"
                    value={selectedIntegration}
                    onChange={onIntegrationChange}
                    optionLabelProp="label"
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    {generatedUnmappedResourceOptions()}
                  </Select>
                </div>
                <UnmappedResourceFiltersButton />
              </div>
              <div className="mb-16 mt-16">
                <Row justify={'space-between'} align={'middle'}>
                  <Col className="text-neutral-60 font-semibold">
                    {t('Integration.unmapped_resources')}{' '}
                    {filteredUnmappedResources.length
                      ? `(${filteredUnmappedResources.length})`
                      : ''}
                  </Col>
                  {/* <Col>
                  <ResourceTypeSelect
                    onChange={handleResourceTypeSelectChange}
                    value={viewModel.activeResourceType}
                    resourceTypeOptions={resourceTypeOptions}
                  />
                </Col> */}
                  <Button
                    type={ButtonType.outline}
                    icon={<HistoryOutlined />}
                    onClick={() => {
                      trackEvent(AmplitudeEventEnum.ACCESS_MATCHING_HISTORY);
                      setIsHistoryModalOpen(true);
                    }}
                    className="bg-white"
                    data-cy="history-button"
                  >
                    {t('resource-mapper.history')}
                  </Button>
                </Row>
              </div>
              {showNoFieldMatchCheckbox && unresolvedToggle}
              <div className="overflow-y-auto overflow-x-hidden flex-1 px-2">
                <UnmappedResourceList
                  unmappedResources={filteredUnmappedResources}
                  status={unmappedResourcesStatus}
                  value={selectedUnmapped}
                  onChange={(value: any) => {
                    trackChangeUnmappedProduct();
                    setSelectedMasterProductId(null);
                    onSelectUnmapped(value);
                  }}
                />
              </div>
            </Content>
            <Footer className="mb-12" />
          </Layout>
        </Sider>
        <Layout>
          {enableFieldMatchingPage && <MisalignmentBanner />}
          {isEmpty ? (
            <EmptyView
              emptyMessage={emptyDataMessage}
              emptyDetails={emptyDataDetail}
              paddingTop="6em"
              emptyState={emptyDataState}
            />
          ) : (
            <>
              <Header className="h-170px w-70% m-auto bg-gray-bg p-0">
                <Col className="pt-18">
                  <Row className="items-center">
                    <span className="text-neutral-100 font-semibold text-16 leading-24px">
                      {t('resource-mapper.make_a_match')}
                    </span>
                    <Tooltip placement="topLeft" title={t('resource-mapper.make_a_match_info')}>
                      <Info className="data-matching-info-icon" />
                    </Tooltip>
                  </Row>
                  <Row className="items-center">
                    <span className="text-neutral-60 text-sm font-normal leading-20px">
                      {t('resource-mapper.make_a_match_description')}
                    </span>
                  </Row>
                  <Row align="middle">
                    <div
                      data-cy="active_resource_display"
                      className={`flex-1 flex justify-between h-40px items-center border-1 border-solid border-neutral-30 rounded-4 pl-10 pt-8 pr-10 pb-8 bg-white ${
                        isLoadingResourceSelect ? 'bg-white cursor-not-allowed' : ''
                      }`}
                    >
                      <span className="inline-block line-normal ">
                        {activeResource?.description}
                      </span>
                      {activeResource?.description ? (
                        <span
                          data-cy="upper_instance_counter"
                          className="inline-block text-neutral-60 line-normal"
                        >
                          {`${selectedInstances.length} ${t('resource-mapper.operation', {
                            count: selectedInstances.length,
                          })}`}
                        </span>
                      ) : (
                        <span className="inline-block text-neutral-60 line-normal">
                          {t('resource-mapper.source')}
                        </span>
                      )}
                    </div>
                    <div className="px-10">
                      <ArrowRightOutlined />
                    </div>
                    <div className="flex-1 flex justify-center">
                      <ResourceMasterListSelect
                        handleClickCreateNewField={handleClickCreateNewField}
                        dropdownClassName={dropdownClassName}
                        selectedUnmappedProductDescription={activeResource?.description}
                        selectedIntegration={selectedIntegration}
                      />
                      <CreateNewField
                        show={visibleCreateNewField}
                        close={handleCancelCreateNewField}
                        activeUser={root?.activeUser}
                      />
                    </div>
                  </Row>
                  <Row className="items-center">
                    <span className="text-neutral-70 font-semibold text-16 leading-24px">
                      {t('resource-mapper.apply_to_operations')}
                    </span>
                    <Tooltip
                      placement="topLeft"
                      title={t('resource-mapper.apply_to_operations_info')}
                    >
                      <Info className="data-matching-info-icon" />
                    </Tooltip>
                  </Row>
                  <Row className="items-center">
                    <span className="text-neutral-60 text-sm font-normal leading-20px">
                      {t('resource-mapper.apply_to_operations_description')}
                    </span>
                  </Row>
                </Col>
              </Header>
              <Content className="mt-12 w-70% h-100% m-auto flex flex-col border-solid border-1 border-neutral-30 rounded-8">
                <Card className="shadow-sm-card z-10 rounded-t-8">
                  <Row>
                    <Space>
                      <Col>
                        <Checkbox
                          data-cy="all_instances_checkbox"
                          id="all_instances_checkbox"
                          checked={allSelectedChecked}
                          indeterminate={indeterminate}
                          onChange={onAllChanged}
                        />
                      </Col>
                      <Col>
                        <label htmlFor="all_instances_checkbox">
                          {`${t('resource-mapper.select_all_inst')} ${
                            selectedInstances.length ? `(${selectedInstances.length})` : ''
                          }`}
                        </label>
                      </Col>
                    </Space>
                  </Row>
                </Card>
                <div ref={cardBodyRef} className="h-100%">
                  <Card className="flex-1 overflow-y-auto rounded-b-8 h-100%">
                    <InstanceCardList
                      status={loadInstancesStatus}
                      allSelected={allSelected}
                      onHeaderClick={onHeaderClick}
                      instances={selectableInstances}
                      activeResourceType={activeResourceType}
                      containerRef={cardBodyRef}
                      instanceProductsMap={instanceProductsMap}
                      loadInstanceProductsStatus={loadInstanceProductsStatus}
                    />
                  </Card>
                </div>
              </Content>
              <Footer className="w-70% m-auto mb-64 px-0">
                <Row justify="space-between" align="middle">
                  <Col data-cy="lower_instance_counter">
                    <Popover
                      className=""
                      placement="top"
                      color="#ffffff"
                      open={isOpenIgnorePopover}
                      onOpenChange={handleOpenChangeIgnorePopover}
                      title={
                        <div className="flex p-10">
                          <WarningIcon />
                          <span className="ml-6 bold text-16">{`${t('resource-mapper.ignore')} ${
                            activeResource?.description
                          }`}</span>
                        </div>
                      }
                      trigger="click"
                      content={
                        <div className="max-w-400px p-10">
                          <div>
                            <span>{t('resource-mapper.ignore_product_text')}</span>
                          </div>
                          <div className="mt-8">
                            <div className="flex justify-end">
                              <Button
                                data-cy="cancel_ignore_product"
                                className="mr-10"
                                type={ButtonType.outline}
                                onClick={() => setOpenIgnorePopover(false)}
                              >
                                {t('Integration.cancel')}
                              </Button>
                              <Button
                                data-cy="ignore_product_button"
                                type={ButtonType.danger}
                                onClick={() => {
                                  handleIgnoreProduct();
                                  setOpenIgnorePopover(false);
                                  trackIgnoreProduct();
                                }}
                              >
                                {t('resource-mapper.ignore_product')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      }
                    >
                      <Button
                        data-cy="ignore_resource_button_trigger"
                        size="large"
                        type={ButtonType.link}
                        disabled={isLoadingIgnoreProduct || selectedInstances.length === 0}
                        style={{ color: '#CF3537' }}
                      >
                        {isLoadingIgnoreProduct && (
                          <Spin delay={100} size="small" spinning className="mr-4" />
                        )}
                        {t('resource-mapper.ignore_product')}
                      </Button>
                    </Popover>
                  </Col>
                  <Col className="text-end">
                    <Row>
                      {isShowFooterOptions() &&
                        showFutureOperationsCheckbox &&
                        futureMappingsCheckbox}
                      <Button
                        data-testid="update_resource_button"
                        size="large"
                        onClick={handleUpdate}
                        type={ButtonType.primary}
                        disabled={isSubmitDisabled}
                      >
                        {isUpdatingResources && (
                          <Spin
                            delay={100}
                            size="small"
                            spinning
                            className="syt-default"
                            style={{ marginRight: 6 }}
                          />
                        )}
                        {t('resource-mapper.update')}
                      </Button>
                    </Row>
                  </Col>
                </Row>
              </Footer>
            </>
          )}
        </Layout>
      </Layout>
      <MatchingHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => {
          trackEvent(AmplitudeEventEnum.RETURN_FROM_MATCHING_HISTORY);
          setIsHistoryModalOpen(false);
        }}
      />
    </>
  );
});
